import { createContext, forwardRef, NamedExoticComponent, Ref, RefAttributes, useContext, useMemo } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { ComponentWithDisplayName, ComponentWithRefContext, DistributiveOmit } from '../utils/types';
import createCache, { EmotionCache, Options as EmotionCacheOptions } from '@emotion/cache';
import { CacheProvider as EmotionCacheProvider } from '@emotion/react';
import { ForwardedRefProps, ForwardedRefType } from '../utils/forwardRefToProps';

/** objects passed through context
 *
 */
export interface PortalData {
  /** The cache used by emotion-js to decide how and where to autogenerate styles
   *
   * This is rooted within the shadow DOM for the portal.
   */
  emotionCache: EmotionCache;
  /** Represents the HTMLElement where portals can be attached while retaining
   * forge and application styling.
   *
   * Fallback logic exists to set this to `document.body` if the div for <Root>
   * cannot be determined. This shouldn't be the case so long as the consumer
   * of RootContainerContext is a child of Root.
   */
  portalNode: HTMLDivElement;
}

export interface PortalContextProps {
  portalData: PortalData;
}

export type PortalContextAndRefProps<RefType> = PortalContextProps & ForwardedRefProps<RefType>;

/** Global emotionCache, for use either when a shadow root does not exist
 * or a Forge component has been used without a <Root> as its parent.
 */
let emotionCacheSingleton: EmotionCache | undefined;

/** Creates a new emotion cache or re-uses emotionCacheSingleton if it's already
 * been defined */
export function createEmotionCache(node?: Node): EmotionCache {
  const baseRules: EmotionCacheOptions = {
    /** so we can better identify emotion rules created on behalf of Forge */
    key: 'fe-c',
    /** Generate styles as text so that rules can be monitored with a
     * MutationObserver */
    speedy: false,
  };
  if (node) {
    return createCache({ ...baseRules, container: node });
  } else {
    if (!emotionCacheSingleton) {
      emotionCacheSingleton = createCache(baseRules);
    }
    return emotionCacheSingleton;
  }
}

/** Creates new PortalData based on the provided portalElement.
 *
 * Used to provide a value to PortalContext.Provider.
 */
export function useNewPortalContext(
  portalElement: HTMLDivElement | null,
  styleElement: HTMLDivElement | null
): PortalData | undefined {
  /** Once portalElement has had a chance to resolve, find the shadow DOM
   * root node, else fall back to `document`.
   *
   * Note that typeof document must be checked because server side rendering
   * with Gatsby does not have access to `document` in the initial render
   */
  return useMemo<PortalData | undefined>(() => {
    if (typeof document !== 'undefined' && portalElement !== null) {
      /** function called with a node to portal to and a node to attach styles to.
       * Create an emotion cache rooted to the style node
       */
      return {
        emotionCache: createEmotionCache(styleElement || undefined),
        portalNode: portalElement,
      };
    }
  }, [portalElement, styleElement]);
}

export type OmitRefAndPortalData<Props> = DistributiveOmit<Props, 'ref' | 'forwardedRef' | 'portalData'>;
export type OmitPortalData<Props> = DistributiveOmit<Props, 'portalData'>;

export type WithPortalDataAndRefReturn<Props> = NamedExoticComponent<
  OmitRefAndPortalData<Props> & RefAttributes<ForwardedRefType<Props>>
>;

/** A Higher Order Component (HOC) used to consume values from PortalContext.
 * Conditionally renders the wrapped FunctionComponent when portalData is available.
 *
 * Passes a non-null portalData as a prop to the provided component.
 *
 * This is important in order to ensure that all required styling is available
 * before rendering child components. Rendering shadow-dom sensitive components
 * without this HOC runs the risk of styling flashes as the emotion-js cache
 * detaches from document.head and re-attaches itself to shadow-root, or portals
 * get created as children of document.body only to reattach themselves as a
 * child of <Root>. A Root component is required so that only one portal is
 * created to document.body.
 */
export function withPortalData<Props extends PortalContextProps>(
  WrappedComponent: ComponentWithDisplayName<Props>
): ComponentWithDisplayName<OmitPortalData<Props>> {
  const WithPortalData: ComponentWithRefContext<OmitPortalData<Props>, ForwardedRefType<Props>> = (props) => {
    const portalData = usePortalContext();

    if (portalData) {
      const wrappedComponentProps = {
        ...props,
        portalData: portalData,
        /** Typescript does not like that typeof props !== Props, hence the
         * aggressive type assertion.
         *
         * > Conversion of to type 'Props' may be a mistake because neither
         * > type sufficiently overlaps with the other
         *
         * In this case, it feels like due diligence has been done to ensure the
         * types are accurate:
         * 1. portalData don't exist in keyof props, but is
         *    added by in this object.
         * 2. All other props are spread across this object.
         */
      } as unknown as Props;

      return (
        <EmotionCacheProvider value={portalData.emotionCache}>
          <WrappedComponent {...wrappedComponentProps} />
        </EmotionCacheProvider>
      );
    } else {
      return null;
    }
  };
  WithPortalData.displayName = `withPortalData(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;
  hoistNonReactStatics(WithPortalData, WrappedComponent);
  return WithPortalData;
}

/** Version of withPortalData that supports ref forwarding. */
export function withPortalDataAndRef<Props extends PortalContextAndRefProps<ForwardedRefType<Props>>>(
  WrappedComponent: ComponentWithDisplayName<Props>
): WithPortalDataAndRefReturn<Props> {
  const WithPortalData: ComponentWithRefContext<OmitRefAndPortalData<Props>, Ref<ForwardedRefType<Props>>> = (
    props,
    ref
  ) => {
    const portalData = usePortalContext();

    if (portalData) {
      const wrappedComponentProps = {
        ...props,
        portalData: portalData,
        forwardedRef: ref,
        /** Typescript does not like that typeof props !== Props, hence the
         * aggressive type assertion.
         *
         * > Conversion of to type 'Props' may be a mistake because neither
         * > type sufficiently overlaps with the other
         *
         * In this case, it feels like due diligence has been done to ensure the
         * types are accurate:
         * 1. forwardedRef and portalData don't exist in keyof props, but are
         *    added by in this object.
         * 2. ref is stripped out by React.forwardRef if it happened to exist.
         * 3. All other props are spread across this object.
         */
      } as unknown as Props;

      return (
        <EmotionCacheProvider value={portalData.emotionCache}>
          <WrappedComponent {...wrappedComponentProps} />
        </EmotionCacheProvider>
      );
    } else {
      return null;
    }
  };
  WithPortalData.displayName = `withPortalDataAndRef(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;
  hoistNonReactStatics(WithPortalData, WrappedComponent);
  return forwardRef(WithPortalData) as WithPortalDataAndRefReturn<Props>;
}

export const defaultPortalContext = undefined;
export const PortalContext = createContext<PortalData | undefined>(defaultPortalContext);

/** Retrieves the ref of the closest Root component in the component hierarchy.
 * Used to ensure component encapsulation within the ShadowDOM.
 */
export const usePortalContext = (): PortalData | undefined => useContext(PortalContext);
